import {IEndPoint} from "/@lib@/commons/io/io.js";
import {IPersistUserDatas} from "/@lib@/commons/registry.js";
import {AuthSrv} from "/@lib@/core/user.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IWsExecFrame} from "/@lib@/core/universe.js";

/**
 * Serveur d'accès aux paramètres et configurations personnelles du user.
 */
export abstract class UserDatasSrv implements IPersistUserDatas {

	protected auth: AuthSrv;

	protected userDatas: Jsonisable;

	constructor(public readonly config: OUserDatasSrvConfig) {}

	/** Permet d'être notifié des changements de user. */
	connectToAuth(auth: AuthSrv) {
		this.auth = auth;
		this.auth.listeners.on("loggedUserChanged", () => this.loadDatas(), -1 /* Mise à dispo des userDatas avant les redraw d'ihm. */);
	}

	/** Permet d'écouter les modifications opérées sur les datas du même user sur un autre device / onglet. */
	connectToWs(ws: IWsExecFrame) {
		//Listener singleton même si plusieurs appels à connectToWs() car univers différents mais même userDatas.
		if (!this._lstn) this._lstn = (m: JUserDatasMsg) => {
			if (m.srcUri.substring(1) === this.getPath()) this.loadDatas();
		};
		ws.msgListeners.on("userDatas", this._lstn);
	}

	protected _lstn?: (m: JUserDatasMsg) => void

	async loadDatas(): Promise<void> {
		if (!this.auth || this.auth.currentUser) {
			try {
				if (this.config.useLocalStorage) {
					const d = localStorage.getItem(this.getLocalStorageKey());
					this.userDatas = d ? JSON.parse(d) as Jsonisable : {};
				} else {
					this.userDatas = (await this.config.userDatasUrl.fetchJson(this.getPath())) || {};
				}
			} catch (e) {
				ERROR.reportError(e);
				this.userDatas = {};
			}
		} else {
			this.userDatas = {};
		}
	}

	getUserData<T extends Jsonisable>(key: string, defaultVal?: T): T {
		return this.userDatas[key];
	}

	async setUserDatas(datas: Dict<Jsonisable>): Promise<void> {
		Object.assign(this.userDatas, datas);
		if (!this.auth || this.auth.currentUser) {
			if (this.config.useLocalStorage) {
				localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(this.userDatas));
			} else {
				await this.config.userDatasUrl.fetch(this.getPath(), 'none', {
					method: "PUT",
					body: JSON.stringify(this.userDatas)
				});
			}
		}
	}

	abstract getLocalStorageKey(): string

	abstract getPath(): string
}

interface JUserDatasMsg {
	svc: 'userDatas',
	srcUri: string,
	event: 'write' | 'delete'
}

export interface OUserDatasSrvConfig {
	useLocalStorage?: boolean
	userDatasUrl?: IEndPoint
	code?: string
}

export class DeskUserDatasSrv extends UserDatasSrv {
	protected _code: string;

	getCode() {
		if (!this._code) this._code = this.config.code || window.desk?.name || 'none';
		return this._code;
	}

	getLocalStorageKey() {
		return `userDatas:desk:${this.getCode()}:${this.auth?.currentAccount || ''}`;
	}

	getPath() { return `front_${this.getCode()}.json`; }
}

export function configUserDatasSrv(authenticatedExecFrameUrl: IEndPoint, config: OUserDatasSrvConfig | UserDatasSrv): OUserDatasSrvConfig | UserDatasSrv {
	if (config === null) return null; //Refus explicite du svc userDatas
	if (config instanceof UserDatasSrv) return config; //Redirection vers un svc userDatas existant
	if (!config) config = {};
	if (!config.userDatasUrl) config.userDatasUrl = authenticatedExecFrameUrl.resolve("u/userDatas/");
	return config;
}
